"use client";

import { useState, useEffect, useRef } from "react";
import styles from "../../styles/case-manager-modules/modal.module.css";
import { SendEmailToProvider } from "../../services/admin-case-manager/readings";
import { ChevronDown } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import Table from "../table";
import toast from "react-hot-toast";
import Input from "../ui/input";
import Modal from "../shared-modules/Modal";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import Button from "../../components/button-v1";
import {
  Dropdown,
  DropdownItem,
  DropdownLabel,
} from "../../components/ui/dropdown";

const SendToProviderModal = ({ isOpen, onClose, selectedPatients, type }) => {
  
  const [formatType, setFormatType] = useState("pdf");
  const [formatDropdownOpen, setFormatDropdownOpen] = useState(false);
  const [patientEmails, setPatientEmails] = useState({});
  const [sendToOtherEmail, setSendToOtherEmail] = useState(false);
  const [universalEmail, setUniversalEmail] = useState("");
  const formatDropdownRef = useRef(null);

  const groupReadingsByPatient = (data) => {
    const grouped = {};

    data.forEach((reading) => {
    
      const patientId = reading.patient;
      if (!patientId) return;

      if (!grouped[patientId]) {
        grouped[patientId] = {
          id: patientId,
          name: reading.patient_full_name || "_ _",
          provider_name: reading.provider_full_name || reading.provider || "_ _",
          provider_email: reading.provider_email || null,
          readings: [],
        };
      }

      grouped[patientId].readings.push({ ...reading, id: reading.id });
    });

    return Object.values(grouped);
  };

  const groupedPatients = groupReadingsByPatient(selectedPatients);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        formatDropdownRef.current &&
        !formatDropdownRef.current.contains(event.target)
      ) {
        setFormatDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const mutation = useMutation(
    (data) => SendEmailToProvider(formatType, data),
    {
      onSuccess: () => {
        toast.success("Email(s) send successfully.!");
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "An error occurred.");
      },
    }
  );

  const handleEmailChange = (patientId, value) => {
    setPatientEmails((prev) => ({ ...prev, [patientId]: value }));
  };

  const handleSend = () => {
    const payload = {
      patient_readings: {},
    };

    groupedPatients.forEach((patient) => {
      const email = sendToOtherEmail
        ? universalEmail
        : patientEmails[patient.id] || patient.provider_email;
      const readingIds = patient.readings.map((r) => r.id.toString());
      payload.patient_readings[patient.id] = {
        email,
        readings: readingIds,
      };
    });


    if (sendToOtherEmail) {
      if (!universalEmail) return toast.error("Email is required");
    } else {
      const hasAllEmails = groupedPatients.every(
        (p) =>
          (patientEmails[p.id] && patientEmails[p.id].trim()) ||
          p.provider_email
      );
      if (!hasAllEmails)
        return toast.error("Email is required for all patients");
    }
    mutation.mutate(payload);
  };

  const formatOptions = [
    { value: "pdf", label: "PDF" },
    { value: "png", label: "PNG" },
  ];

  const columns = [
    { key: "date", label: "Date", alignLeft: true },
    { key: "type", label: "Reading Type", sorting: false },
    { key: "reading_value", label: "Value", alignLeft: true },
  ];

  const modalContent = (
    <form>
      <h2 className={styles.readingsTitle2}>
        {type === "escalated" ? "Escalated" : "Reviewed"} Readings
      </h2>

      <div className={styles.formControls}>
        <div className={styles.formGroup}>
          <label>Format Type</label>
          <Dropdown
            onChange={(value) => setFormatType(value)}
            value={formatType}
            options={[
              {
                label: "PDF",
                value: "pdf",
              },
              {
                label: "PNG",
                value: "png",
              },
            ]}
          ></Dropdown>
        </div>

        <div className={styles.formGroup}></div>
      </div>

      <div className={styles.formControls}>
        <div className={styles.formGroup}>
          <div className={styles.other_email_section}>
            <input
              type="checkbox"
              checked={sendToOtherEmail}
              onChange={(e) => setSendToOtherEmail(e.target.checked)}
              // style={{ marginRight: "8px" }}
            />
            <label>Send all to other email</label>
          </div>

          {sendToOtherEmail && (
            <>
              {/* <label>Send on other email</label> */}

              <input
                type="email"
                placeholder="Enter email"
                value={universalEmail}
                onChange={(e) => setUniversalEmail(e.target.value)}
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </>
          )}
        </div>

        <div className={styles.formGroup}></div>
      </div>

      {groupedPatients.map((patient) => (
        <div
          key={patient.id}
          style={{
            marginBottom: "2rem",
            border: "1px solid #eee",
            padding: "1rem",
            borderRadius: "8px",
          }}
        >
          <div style={{ marginBottom: "0.5rem", fontWeight: "bold" }}>
            {patient.name}
          </div>

          <div className={styles.formControls}>
            <div className={styles.formGroup}>
              <label>Provider: {patient.provider_name}</label>
              <div style={{ opacity: sendToOtherEmail ? 0.5 : 1 }}>
                <label style={{ marginRight: "10px" }}>Email: </label>
                <input
                  type="email"
                  value={patientEmails[patient.id] || patient.provider_email}
                  onChange={(e) =>
                    handleEmailChange(patient.id, e.target.value)
                  }
                  // disabled={sendToOtherEmail}4
                  disabled={universalEmail}
                  style={{
                    padding: "6px",
                    color: "#626972",
                    width: "300px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "1rem" }}></div>

          <div className={styles.tableContainer}>
            <Table
              columns={columns}
              data={patient?.readings}
              isRowClickable={false}
            />
          </div>
        </div>
      ))}
    </form>
  );

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
        </DialogHeader>
        <DialogBody>{modalContent}</DialogBody>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost">
            Close
          </Button>
          <Button
            disabled={
              (sendToOtherEmail && !universalEmail) ||
              (!sendToOtherEmail &&
                !groupedPatients.every(
                  (p) =>
                    (patientEmails[p.id] && patientEmails[p.id].trim()) ||
                    p.provider_email
                ))
            }
            loading={mutation.isLoading}
            onClick={handleSend}
          >
            Send All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendToProviderModal;
